/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import {
  $NodeGroupFilters,
  GetNodeGroupsData,
  GetNodeGroupsResponse,
  NodeGroup,
  NodeGroupFilters,
  NodeGroupSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedNodeGroupsQueryParams = {
  params?: Omit<GetNodeGroupsData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated node groups with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated node groups response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedNodeGroupsQuery({ params, type }: UsePaginatedNodeGroupsQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<
    GetNodeGroupsResponse,
    NodeGroup,
    NodeGroupFilters,
    NodeGroupSortableFields,
    GetNodeGroupsData
  >({
    apiCall: mgmtPlaneAPIClient.nodeGroups.getNodeGroups.bind(mgmtPlaneAPIClient.nodeGroups),
    filterTypes: jsonSchemaToTypeMap($NodeGroupFilters),
    queryKey: 'nodeGroups',
    realtimeEvents: ['nodeGroup:created', 'nodeGroup:updated', 'nodeGroup:deleted'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
